import React, { useState } from "react";
import { ChevronDown } from "lucide-react";

/**
 * CollectiveStandards — the culture statement + collapsible community
 * guidelines that live above The Collective feed.
 *
 * Design intent: this is atmosphere, not a legal wall. The always-visible
 * line stays calm and short; the guidelines expand only when the reader
 * asks for them, then land as short lines with warm eyebrow labels — not
 * bullet points, not a card that dominates the page.
 */
const RULES = [
  { label: "No hype",              body: "No exaggerated claims, promises, or pressure." },
  { label: "No solicitation",      body: "No selling, recruiting, promoting services, or seeking clients." },
  { label: "Share, don't sell",    body: "Share what has worked for you without turning your experience into an advertisement." },
  { label: "Support, don't compare", body: "Everyone's journey is different. Encourage rather than judge." },
  { label: "Professionals",        body: "Professionals are welcome to share knowledge and rituals, but may not use the Collective to solicit, promote services, recruit members, or seek clients." },
];

export default function CollectiveStandards() {
  const [open, setOpen] = useState(false);

  return (
    <section className="reveal" data-testid="collective-standards">
      <p
        className="font-display italic text-lg md:text-xl text-[hsl(var(--fg))]/85 leading-snug"
        data-testid="collective-standards-statement"
      >
        Real experiences over hype.{" "}
        <span className="text-[hsl(var(--clay))]">Connection over promotion.</span>
      </p>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls="community-guidelines-panel"
        data-testid="collective-standards-toggle"
        className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border hairline text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:border-[hsl(var(--clay))]/40 tab-link"
      >
        Community guidelines
        <ChevronDown
          size={12}
          className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>

      <div
        id="community-guidelines-panel"
        role="region"
        aria-hidden={!open}
        data-testid="collective-standards-panel"
        style={{
          maxHeight: open ? "800px" : "0px",
          opacity: open ? 1 : 0,
          transition: "max-height 400ms ease, opacity 300ms ease",
        }}
        className="overflow-hidden"
      >
        <div className="mt-5 pl-4 border-l-2 border-[hsl(var(--clay))]/30 space-y-5 max-w-2xl">
          <p className="text-[15px] leading-relaxed text-[hsl(var(--fg))]/85" data-testid="collective-standards-intro">
            Our Collective is built on real experiences, honest questions, and genuine support.
          </p>
          <p className="font-display italic text-lg text-[hsl(var(--clay))]" data-testid="collective-standards-mantra">
            Keep it real. Keep it respectful. Keep it intentional.
          </p>

          <ul className="space-y-4">
            {RULES.map((r) => (
              <li key={r.label} data-testid={`collective-rule-${r.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                <div className="text-[11px] uppercase tracking-[0.24em] text-[hsl(var(--terracotta))]">
                  {r.label}
                </div>
                <p className="mt-1 text-[14px] leading-relaxed text-[hsl(var(--fg))]/80">
                  {r.body}
                </p>
              </li>
            ))}
          </ul>

          <p
            className="pt-2 text-[13px] italic text-[hsl(var(--muted-foreground))] leading-relaxed"
            data-testid="collective-standards-report"
          >
            See something that doesn&rsquo;t belong? Report it.
          </p>
        </div>
      </div>
    </section>
  );
}
